import { Auth } from "@/hooks/auth";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Calendar,
  CalendarClock,
  Briefcase,
  GitFork,
  Building2,
  File,
  Contact,
  HandHelping,
  FileStack,
  Copy,
  Settings,
  LogOut,
  ListChecks,
  Megaphone,
  PanelLeft,
  Search,
  ChevronsUpDown,
  Check,
  MoreHorizontal,
  Menu,
  X,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useEffect, useState } from "react";
import { resetAuthUiState } from "@/slices/auth/authUi";
import { supabase } from "@/utilities/supabase";
import { setActiveTeam, setIsOwner } from "@/slices/team/teamUi";
import { useGetCompaniesQuery } from "@/slices/company/companyApi";
import { useGetUserByIdQuery } from "@/slices/users/usersApi";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useGetDealsSummaryQuery } from "@/slices/deals/dealsApi";
import { useGetClientNeedsQuery } from "@/slices/clientNeeds/clientNeedsApi";
import { useGetListingSheetsQuery } from "@/slices/listingSheets/listingSheetsApi";
import { useView } from "@/hooks/useView";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { VIEW_META, VIEW_ORDER, type RepsityView } from "@/lib/views";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { AvatarWithFallback } from "@/components/Avatar";
import { useAuthenticatedImage } from "@/hooks/useAuthenticatedImage";
import { InviteModalProvider } from "@/components/InviteModal/InviteModalContext";
import {
  GlobalSearchProvider,
  useGlobalSearch,
} from "@/components/GlobalSearch";
import {
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";

/* ──────────────────────────────────────────────────────────────────────────
 * Redesigned navigation sidebar.
 *
 * Reuses the existing `SidebarProvider`/`useSidebar` primitive purely for its
 * collapse cookie (`sidebar:state`) + ⌘B toggle + mobile open state — the
 * visual chrome below is fully custom to the green-surface redesign
 * (see research/home-redesign/SIDEBAR_REDESIGN_HANDOFF.md §2–4). Global search
 * (⌘K) is mounted once here via <GlobalSearchProvider> so it wraps all routes;
 * the search rows call useGlobalSearch().open().
 * ────────────────────────────────────────────────────────────────────────── */

interface ActiveTeam {
  id: string;
  name?: string;
  ownerId?: string;
}

interface NavItemDef {
  name: string;
  path: string;
  icon: LucideIcon;
  /** Live count badge (e.g. active deals, open needs). Hidden at 0/undefined. */
  count?: number;
  /** Render the count badge in the urgent (mint) variant. */
  urgent?: boolean;
  /** "View as" lens gate — the item shows only in these views. Omit = always
   *  visible. A VIEW filter, not access control (the route stays reachable). */
  views?: RepsityView[];
}

interface PlatformWithSidebarProps {
  collapsed?: boolean;
  /**
   * Drop the navigation entirely. For focused, one-task surfaces (the
   * marketing builder) where even the collapsed icon rail is a competing
   * exit — the page carries its own Back control.
   */
  chromeless?: boolean;
}

export const PlatformWithSidebar = ({
  collapsed = false,
  chromeless = false,
}: PlatformWithSidebarProps) => {
  if (chromeless) {
    return (
      <Auth>
        <GlobalSearchProvider>
          <InviteModalProvider>
            <div className="min-h-screen bg-canvas">
              <Outlet />
            </div>
          </InviteModalProvider>
        </GlobalSearchProvider>
      </Auth>
    );
  }

  return (
    <Auth>
      {/* `defaultOpen={!collapsed}` seeds the rail state for the routes that
          opt into the collapsed variant (e.g. /documents, /workflows) while
          still honoring the persisted cookie for the rest. */}
      <SidebarProvider defaultOpen={!collapsed}>
        <GlobalSearchProvider>
          <InviteModalProvider>
            {/* `collapsed` routes (documents/workflows/tours) are full-bleed —
                the page owns its own h-screen layout, so we skip the content
                card wrapper for them (matches the old two-branch behavior). */}
            <PlatformChrome fullBleed={collapsed} />
          </InviteModalProvider>
        </GlobalSearchProvider>
      </SidebarProvider>
    </Auth>
  );
};

// Dark popover styling so the workspace + account menus match the green
// sidebar (the cmdk/Radix DropdownMenu primitives default to a light surface).
// tailwind-merge lets these override the conflicting light defaults.
// z-[140] lifts every menu ABOVE the mobile drawer (z-[120]) + top bar
// (z-[130]) — the Radix content portals to <body> at a default z-50, which
// otherwise renders BEHIND the drawer on mobile (nothing appears to pop).
const DARK_MENU = "z-[140] border-white/10 bg-[#1d3a2b] text-white shadow-2xl";
// `data-[state=open]:*` covers the submenu TRIGGER while its flyout is open —
// the shadcn default turns it neutral-100 (white/unreadable) otherwise.
const DARK_MENU_ITEM =
  "text-[rgba(223,238,229,0.9)] focus:bg-white/10 focus:text-white data-[state=open]:bg-white/10 data-[state=open]:text-white";
const DARK_MENU_SEP = "bg-white/10";

function PlatformChrome({ fullBleed = false }: { fullBleed?: boolean }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { state, toggleSidebar, openMobile, setOpenMobile, isMobile } =
    useSidebar();
  const { open: openSearch } = useGlobalSearch();
  // "View as" lens (design_handoff_view_switcher) — drives <ViewGate> + nav
  // filtering. A VIEW, never access control.
  const { view, setView } = useView();
  const activeViewMeta = VIEW_META[view];

  // One view-option row, shared by the desktop flyout + the mobile inline list.
  // The dot carries a light ring so the blue (Brokerage) reads on the dark-green
  // menu surface.
  const renderViewOption = (v: RepsityView) => {
    const meta = VIEW_META[v];
    const active = v === view;
    return (
      <DropdownMenuItem
        key={v}
        className={`${DARK_MENU_ITEM} items-start gap-2.5`}
        onClick={() => setView(v)}
      >
        <span
          className={`mt-1 h-2.5 w-2.5 flex-none rounded-full ring-1 ring-white/60 ${meta.dotClass}`}
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-[13px] font-semibold">{meta.label}</span>
            {active && (
              <Check
                className="h-3.5 w-3.5 flex-none text-white"
                strokeWidth={2.5}
              />
            )}
          </span>
          <span className="mt-0.5 block text-[11px] leading-snug text-white/55">
            {meta.roleLine}
          </span>
          <span className="mt-0.5 block text-[11px] leading-snug text-white/40">
            {meta.blurb}
          </span>
        </span>
      </DropdownMenuItem>
    );
  };

  const railed = state === "collapsed";

  // Content-card sizing — pages opt into full-width / tight padding via the
  // layout slice (matches the Catalyst SidebarLayout the redesigned pages
  // bleed into with `-m-6`). Restoring this wrapper is what fixes the content
  // overflow + the green backdrop leaking onto un-redesigned pages.
  const isWidePage = useAppSelector((s) => s.layoutUi?.isWidePage ?? false);
  const isTightPage = useAppSelector((s) => s.layoutUi?.isTightPage ?? false);

  const [team, setTeam] = useState<ActiveTeam | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  /* API */
  const { data: userRes } = useGetUserByIdQuery(userId ?? skipToken);
  const { data: companyRes, isFetching: isCompanyResFetching } =
    useGetCompaniesQuery({});
  const { data: dealsSummary } = useGetDealsSummaryQuery();
  const { data: needsRes } = useGetClientNeedsQuery({
    status: "OPEN",
    limit: 100,
  });
  const { data: listingsRes } = useGetListingSheetsQuery({
    status: "PUBLISHED",
    limit: 100,
  });

  /* Count badges (hide at 0/loading). */
  const activeDeals: number | undefined =
    dealsSummary?.data?.activeDeals || undefined;
  const openNeeds: number | undefined =
    (needsRes?.data?.data?.length as number | undefined) || undefined;
  const publishedListings: number | undefined =
    listingsRes?.data?.data?.length || undefined;

  useEffect(() => {
    const getUser = async () => {
      const { data } = await supabase.auth.getSession();
      setUserId(data?.session?.user?.id || null);
    };
    getUser();
  }, []);

  useEffect(() => {
    // Green gradient backdrop on dashboard pages.
    document.documentElement.classList.add("dashboard-layout");
    return () => {
      document.documentElement.classList.remove("dashboard-layout");
    };
  }, []);

  useEffect(() => {
    if (companyRes?.data?.[0]?.id && userId) {
      const activeTeam = companyRes.data[0];
      dispatch(setActiveTeam(activeTeam));
      setTeam(activeTeam);
      dispatch(setIsOwner(activeTeam.ownerId === userRes?.person?.id));
    }
  }, [userRes, companyRes, dispatch, userId]);

  const logout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
    dispatch(resetAuthUiState());
  };

  // Primary "Workspace" group — the day-to-day demand/supply surface. Needs
  // (/needs, demand) and Listings (/listings, supply) sit adjacent here so the
  // two sides of the NEEDS marketplace read as a pair (REP-206 §3).
  const businessItems: NavItemDef[] = [
    { name: "Dashboard", path: "/", icon: Home },
    { name: "Assistant", path: "/assistant", icon: Sparkles },
    { name: "Deals", path: "/deals", icon: Briefcase, count: activeDeals },
    // Flow — where the deals above came from. Sits directly under Deals
    // because it only makes sense as the answer to "and where did these
    // come from?". Brokerage lens only: a participant isn't running a book.
    { name: "Flow", path: "/flow", icon: GitFork, views: ["brokerage"] },
    { name: "Properties", path: "/properties", icon: Building2 },
    { name: "Needs", path: "/needs", icon: HandHelping, count: openNeeds },
    {
      name: "Listings",
      path: "/listings",
      icon: Megaphone,
      count: publishedListings,
      views: ["brokerage"],
    },
    // Tours are first-class (REP-206 §6) — they get a Workspace entry, not a
    // Tools/Schedule sub-surface. The page aggregates every TOUR Event the
    // user owns (standalone + deal-sourced); see /tours.
    { name: "Tours", path: "/tours", icon: CalendarClock },
    { name: "Contacts", path: "/contacts", icon: Contact },
  ];

  const toolItems: NavItemDef[] = [
    {
      name: "Lists",
      path: "/lists",
      icon: ListChecks,
      views: ["brokerage", "customer"],
    },
    { name: "Schedule", path: "/schedule", icon: Calendar },
    { name: "Documents", path: "/documents", icon: File },
  ];

  const adminItems: NavItemDef[] = [
    {
      name: "Workflows",
      path: "/templates/workflows",
      icon: FileStack,
      views: ["brokerage", "provider"],
    },
    {
      name: "Templates",
      path: "/templates/documents",
      icon: Copy,
      views: ["brokerage", "provider"],
    },
    { name: "Settings", path: "/settings", icon: Settings },
  ];

  const navGroups: { title: string; items: NavItemDef[] }[] = [
    { title: "Workspace", items: businessItems },
    { title: "Tools", items: toolItems },
    { title: "Admin", items: adminItems },
  ];

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/" || location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(path);
  };

  const go = (path: string) => {
    navigate(path);
    setOpenMobile(false);
  };

  const teamName = isCompanyResFetching
    ? "Loading…"
    : team?.name || "Personal Team";
  const monogram = (team?.name || "Personal Team")
    .split(" ")
    .slice(0, 2)
    .map((w: string) => w[0])
    .join("")
    .toUpperCase();

  // Workspace icon — show the company's uploaded logo if one exists, otherwise
  // fall back to the monogram chip. avatarRef lives on the active company and
  // resolves through the authenticated asset stream (folder "avatars").
  const { imageUrl: workspaceImageUrl } = useAuthenticatedImage(
    (team as { avatarRef?: string } | null)?.avatarRef,
    "avatars",
  );

  const userInitials =
    userRes?.person?.name
      ?.split(" ")
      .slice(0, 2)
      .map((n: string) => n[0])
      .join("") || " ";

  return (
    <TooltipProvider delayDuration={0}>
    <div className="flex h-screen w-full bg-[#112819]">
      {/* ── Mobile top bar (≤880px) ─────────────────────────────────────── */}
      <header
        className={[
          "fixed inset-x-0 top-0 z-[130] flex items-center gap-2 px-3 py-2.5 min-[881px]:hidden",
          "transition-colors duration-200",
          openMobile
            ? "bg-transparent"
            : "border-b border-zinc-200 bg-[rgba(251,251,252,0.9)] backdrop-blur-md",
        ].join(" ")}
      >
        <button
          type="button"
          onClick={() => setOpenMobile(!openMobile)}
          aria-label={openMobile ? "Close menu" : "Open menu"}
          aria-expanded={openMobile}
          className={[
            "flex h-[42px] w-[42px] flex-none items-center justify-center rounded-[10px] border border-transparent transition-colors",
            openMobile
              ? "text-[#42E08C] hover:bg-white/10"
              : "text-zinc-700 hover:bg-zinc-100",
          ].join(" ")}
        >
          {openMobile ? (
            <X className="h-[22px] w-[22px]" strokeWidth={2} />
          ) : (
            <Menu className="h-[21px] w-[21px]" strokeWidth={2} />
          )}
        </button>
        <img
          src={
            openMobile ? "/repsity-sidebar.svg" : "/repsity-logo-dark.svg"
          }
          alt="Repsity"
          className="h-[22px]"
        />
        {!openMobile && (
          <button
            type="button"
            onClick={openSearch}
            aria-label="Search"
            className="ml-auto flex h-[42px] w-[42px] flex-none items-center justify-center rounded-[10px] border border-transparent text-zinc-700 transition-colors hover:bg-zinc-100"
          >
            <Search className="h-[19px] w-[19px]" strokeWidth={2} />
          </button>
        )}
      </header>

      {/* ── Mobile scrim-less full-screen surface uses the same aside below.
            On desktop it is the persistent column. ─────────────────────── */}
      <aside
        data-railed={railed}
        className={[
          // Shared green surface.
          "relative z-[120] flex flex-col text-[#DCEBE2]",
          "bg-[linear-gradient(177deg,#1d3f2e_0%,#163220_60%,#112819_100%)]",
          // Desktop column.
          "min-[881px]:z-[50] min-[881px]:h-screen min-[881px]:shadow-[inset_-1px_0_0_rgba(255,255,255,0.055)]",
          "transition-[width,transform,opacity] duration-200 ease-out",
          railed ? "min-[881px]:w-[74px]" : "min-[881px]:w-[252px]",
          // Mobile full-screen takeover.
          "max-[880px]:fixed max-[880px]:inset-0 max-[880px]:h-[100dvh] max-[880px]:w-auto max-[880px]:overflow-y-auto",
          openMobile
            ? "max-[880px]:translate-y-0 max-[880px]:opacity-100"
            : "max-[880px]:pointer-events-none max-[880px]:-translate-y-2.5 max-[880px]:opacity-0",
        ].join(" ")}
      >
        {/* top sheen */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[170px] bg-[radial-gradient(120%_70%_at_22%_0%,rgba(66,224,140,0.10),transparent_72%)]"
        />

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div
          className={[
            "relative flex flex-col gap-3 px-4 pb-3 pt-[18px]",
            "max-[880px]:pt-16",
            railed ? "min-[881px]:items-center min-[881px]:px-0" : "",
          ].join(" ")}
        >
          {/* Brand row (hidden on mobile — the top bar X is the toggle) */}
          <div
            className={[
              "flex min-h-[28px] items-center justify-between max-[880px]:hidden",
              railed ? "min-[881px]:flex-col min-[881px]:gap-3" : "",
            ].join(" ")}
          >
            <img
              src={railed ? "/repsity-icon.svg" : "/repsity-sidebar.svg"}
              alt="Repsity"
              className={railed ? "h-[27px] w-[27px]" : "h-[23px] w-auto"}
            />
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label={railed ? "Expand sidebar" : "Collapse sidebar"}
              className="group/tip relative flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg border border-transparent text-[rgba(220,235,226,0.6)] transition-colors hover:bg-white/10 hover:text-white"
            >
              <PanelLeft className="h-[17px] w-[17px]" strokeWidth={1.9} />
              <Tip label={railed ? "Expand" : "Collapse"} railed={railed} />
            </button>
          </div>

          {/* Workspace switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={[
                  "group/tip relative flex w-full items-center gap-2.5 rounded-[11px] border border-white/[0.08] bg-white/[0.06] px-2.5 py-[7px] text-left text-white transition-colors hover:border-white/[0.16] hover:bg-white/10",
                  railed
                    ? "min-[881px]:h-[38px] min-[881px]:w-[38px] min-[881px]:justify-center min-[881px]:px-0"
                    : "",
                ].join(" ")}
              >
                  {workspaceImageUrl ? (
                    <img
                      src={workspaceImageUrl}
                      alt=""
                      className="h-[30px] w-[30px] flex-none rounded-lg object-cover shadow-[0_1px_2px_rgb(0_0_0/0.28),inset_0_1px_0_rgba(255,255,255,0.4)]"
                    />
                  ) : (
                    <span className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg bg-[linear-gradient(150deg,#c4f2d7,#5ad597)] text-[12px] font-bold text-[#0e2417] shadow-[0_1px_2px_rgb(0_0_0/0.28),inset_0_1px_0_rgba(255,255,255,0.4)]">
                      {monogram || "PT"}
                    </span>
                  )}
                  <span
                    className={[
                      "flex min-w-0 flex-1 flex-col",
                      railed ? "min-[881px]:hidden" : "",
                    ].join(" ")}
                  >
                    <span className="truncate text-[13px] font-semibold leading-tight">
                      {teamName}
                    </span>
                  </span>
                  <ChevronsUpDown
                    className={[
                      "h-[15px] w-[15px] flex-none text-[rgba(220,235,226,0.6)]",
                      railed ? "min-[881px]:hidden" : "",
                    ].join(" ")}
                    strokeWidth={2}
                  />
                  <Tip label={teamName} railed={railed} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side={railed ? "right" : "bottom"}
              className={`w-56 ${DARK_MENU}`}
            >
              <DropdownMenuLabel className="text-white">
                {teamName}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className={DARK_MENU_SEP} />
              <DropdownMenuItem
                className={DARK_MENU_ITEM}
                onClick={() => go("/settings/company/company-profile")}
              >
                <Settings className="mr-2 h-4 w-4" />
                Workspace Settings
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Search trigger */}
          <button
            type="button"
            onClick={openSearch}
            className={[
              "group/tip relative flex w-full items-center gap-2.5 rounded-[10px] border border-white/[0.08] bg-black/[0.16] px-2.5 py-2 text-[rgba(220,235,226,0.6)] transition-colors hover:border-white/[0.14] hover:bg-black/[0.24] hover:text-[#DCEBE2]",
              railed
                ? "min-[881px]:h-[38px] min-[881px]:w-[38px] min-[881px]:justify-center min-[881px]:px-0"
                : "",
            ].join(" ")}
          >
              <Search className="h-4 w-4 flex-none" strokeWidth={1.9} />
              <span
                className={[
                  "flex-1 text-left text-[13px] font-normal",
                  railed ? "min-[881px]:hidden" : "",
                ].join(" ")}
              >
                Search
              </span>
              <kbd
                className={[
                  "rounded-[5px] border border-white/[0.16] bg-white/[0.05] px-[5px] py-[3px] text-[10.5px] font-semibold leading-none text-[rgba(220,235,226,0.6)]",
                  railed ? "min-[881px]:hidden" : "",
                ].join(" ")}
              >
                ⌘K
              </kbd>
              <Tip label="Search" railed={railed} />
            </button>
        </div>

        {/* ── Nav body ───────────────────────────────────────────────── */}
        <nav
          className={[
            "flex flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden px-3 pb-3.5 pt-2.5",
            // The rail used to be `overflow-visible` so the hover labels (which
            // sit outside its 74px width) weren't clipped — but that also meant
            // it couldn't scroll, so a long nav overflowed the viewport. It now
            // scrolls like the expanded sidebar; the labels are portaled out via
            // Radix instead of relying on the overflow (see SidebarItem).
            railed ? "min-[881px]:items-center min-[881px]:px-0" : "",
          ].join(" ")}
        >
          {navGroups.map((group) => {
            // "View as" lens — hide items not in the active view; drop the whole
            // group (heading included) if nothing's left. A VIEW filter, not
            // access control — the routes stay reachable by deep-link.
            const visibleItems = group.items.filter(
              (i) => !i.views || i.views.includes(view)
            );
            if (visibleItems.length === 0) return null;
            return (
              <div
                key={group.title}
                className={[
                  "flex flex-col gap-0.5",
                  railed ? "min-[881px]:w-full min-[881px]:items-center" : "",
                ].join(" ")}
              >
                <p
                  className={[
                    "mb-[5px] ml-[11px] text-[10px] font-bold uppercase tracking-[0.11em] text-[rgba(220,235,226,0.4)]",
                    railed
                      ? "min-[881px]:mx-auto min-[881px]:my-2 min-[881px]:h-px min-[881px]:w-8 min-[881px]:overflow-hidden min-[881px]:bg-white/[0.08] min-[881px]:text-transparent"
                      : "",
                  ].join(" ")}
                >
                  {group.title}
                </p>
                {visibleItems.map((item) => (
                  <NavItem
                    key={item.path}
                    item={item}
                    active={isActive(item.path)}
                    railed={railed}
                    onClick={() => go(item.path)}
                  />
                ))}
              </div>
            );
          })}
        </nav>

        {/* ── Footer user menu ───────────────────────────────────────── */}
        <div
          className={[
            "relative mx-2.5 mb-2.5",
            railed ? "min-[881px]:mx-auto" : "",
          ].join(" ")}
        >
          <span
            aria-hidden
            className="absolute inset-x-2 -top-1.5 h-px bg-white/[0.08]"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={[
                  "group/tip relative flex w-full items-center gap-2.5 rounded-[11px] border border-transparent p-2 text-left transition-colors hover:border-white/[0.08] hover:bg-white/[0.06]",
                  railed
                    ? "min-[881px]:h-[46px] min-[881px]:w-[46px] min-[881px]:justify-center"
                    : "",
                ].join(" ")}
              >
                  <AvatarWithFallback
                    className="h-8 w-8 flex-none ring-2 ring-white/20"
                    image={userRes?.person?.avatarRef}
                    emptyImageText={userInitials}
                  />
                  <span
                    className={[
                      "flex min-w-0 flex-1 flex-col",
                      railed ? "min-[881px]:hidden" : "",
                    ].join(" ")}
                  >
                    <span className="truncate text-[13px] font-semibold leading-tight text-white">
                      {userRes?.person?.name || "Loading…"}
                    </span>
                    <span className="truncate text-[11px] text-[rgba(220,235,226,0.6)]">
                      {userRes?.email}
                    </span>
                  </span>
                  <MoreHorizontal
                    className={[
                      "h-4 w-4 flex-none text-[rgba(220,235,226,0.6)]",
                      railed ? "min-[881px]:hidden" : "",
                    ].join(" ")}
                    strokeWidth={2}
                  />
                  <Tip
                    label={userRes?.person?.name || "Account"}
                    railed={railed}
                  />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side={railed ? "right" : "top"}
              className={`w-56 ${DARK_MENU}`}
            >
              <DropdownMenuLabel className="text-white">
                <div className="flex flex-col">
                  <span>{userRes?.person?.name || "My Account"}</span>
                  <span className="text-xs font-normal text-[rgba(220,235,226,0.55)]">
                    {userRes?.email}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className={DARK_MENU_SEP} />
              <DropdownMenuGroup>
                {/* "View as" lens — a VIEW, not access control. On MOBILE a
                    side-flyout runs off a phone screen, so the views render
                    INLINE; on desktop they stay a tidy flyout submenu. */}
                {isMobile ? (
                  <>
                    <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/50">
                      Viewing as
                    </DropdownMenuLabel>
                    {VIEW_ORDER.map((v) => renderViewOption(v))}
                    <p className="px-2 pb-1 pt-1 text-[11px] leading-snug text-white/45">
                      A view only changes what you see — you keep full access.
                    </p>
                  </>
                ) : (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className={DARK_MENU_ITEM}>
                      <span
                        className={`mr-2 h-2 w-2 flex-none rounded-full ring-1 ring-white/60 ${activeViewMeta.dotClass}`}
                      />
                      <span className="flex-1">Viewing as</span>
                      <span className="ml-2 text-[rgba(220,235,226,0.6)]">
                        {activeViewMeta.label}
                      </span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent
                        className={`w-64 ${DARK_MENU}`}
                        sideOffset={4}
                      >
                        <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/50">
                          Switch view
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator className={DARK_MENU_SEP} />
                        {VIEW_ORDER.map((v) => renderViewOption(v))}
                        <DropdownMenuSeparator className={DARK_MENU_SEP} />
                        <p className="px-2 py-1.5 text-[11px] leading-snug text-white/45">
                          A view only changes what you see. You keep full access
                          to everything in Repsity.
                        </p>
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
                <DropdownMenuSeparator className={DARK_MENU_SEP} />
                <DropdownMenuItem
                  className={DARK_MENU_ITEM}
                  onClick={() => go("/settings")}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem className={DARK_MENU_ITEM} onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────────────────────────
          Two modes (mirrors the old layout's two branches):
          • fullBleed (documents/workflows/tours): the page owns its own
            h-screen layout — render the Outlet bare. The green backdrop shows
            through until the editor mounts (the doc "green loading" state).
          • default: wrap the Outlet in the Catalyst content card
            (rounded white panel + ring/shadow, framed by the green gutter).
            Redesigned pages bleed gray over it via `-m-6`; un-redesigned pages
            sit inside the white panel. `isTightPage`/`isWidePage` drive the
            padding + max-width exactly like the Catalyst SidebarLayout did. */}
      <main className="flex min-w-0 flex-1 flex-col overflow-y-auto bg-white max-[880px]:pt-[60px] lg:bg-transparent">
        {fullBleed ? (
          <Outlet />
        ) : (
          // Flex chain (main → gutter → card → inner) so the white card always
          // fills the viewport height — short pages no longer reveal the green
          // backdrop below their content. `flex-1` gives each level a definite
          // height, which is what lets the redesigned pages' `min-h-full` work.
          <div className="flex flex-1 flex-col lg:p-2">
            <div
              className={[
                "flex flex-1 flex-col p-6 lg:rounded-lg lg:bg-white lg:shadow-xs lg:ring-1 lg:ring-zinc-950/5",
                isTightPage ? "lg:p-6" : "lg:p-10",
              ].join(" ")}
            >
              <div
                className={
                  isWidePage
                    ? "flex w-full flex-1 flex-col"
                    : "mx-auto w-full max-w-6xl flex-1"
                }
              >
                <Outlet />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
    </TooltipProvider>
  );
}

/* Single nav row — pill active state, optional count badge, rail tooltip. */
function NavItem({
  item,
  active,
  railed,
  onClick,
}: {
  item: NavItemDef;
  active: boolean;
  railed: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  const showCount = typeof item.count === "number" && item.count > 0;

  const button = (
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? "page" : undefined}
        className={[
          "group/tip relative flex w-full items-center gap-[11px] rounded-[9px] px-[11px] text-[13.5px] font-medium transition-colors",
          railed
            ? "min-[881px]:h-[46px] min-[881px]:w-[46px] min-[881px]:justify-center min-[881px]:gap-0 min-[881px]:px-0"
            : "",
          "h-[38px] max-[880px]:h-[46px] max-[880px]:text-[14.5px]",
          active
            ? "bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
            : "text-[#DCEBE2] hover:bg-white/[0.06] hover:text-white",
        ].join(" ")}
      >
        <Icon
          className={[
            "flex-none transition-colors",
            active
              ? "text-white"
              : "text-[rgba(220,235,226,0.6)] group-hover/tip:text-white",
          ].join(" ")}
          style={{ width: 18, height: 18 }}
          strokeWidth={1.85}
        />
        <span
          className={[
            "min-w-0 flex-1 truncate text-left",
            railed ? "min-[881px]:hidden" : "",
          ].join(" ")}
        >
          {item.name}
        </span>
        {showCount && (
          <span
            className={[
              "flex h-[19px] min-w-[21px] flex-none items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums",
              item.urgent
                ? "bg-[linear-gradient(150deg,#c4f2d7,#5ad597)] text-[#0e2417]"
                : "bg-white/[0.08] text-[rgba(220,235,226,0.6)]",
              railed ? "min-[881px]:hidden" : "",
            ].join(" ")}
          >
            {item.count}
          </span>
        )}
      </button>
  );

  // Railed: the label can't live inside the (now scrollable) rail without being
  // clipped, so render it through Radix's portal instead of an absolute child.
  if (!railed) return button;

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={14}
        className="hidden whitespace-nowrap rounded-[7px] border border-white/[0.09] bg-[#0d2014] px-2.5 py-1.5 text-[12px] font-medium text-[#eafff2] shadow-[0_8px_22px_rgb(0_0_0/0.4)] min-[881px]:block"
      >
        {item.name}
      </TooltipContent>
    </Tooltip>
  );
}

/* Rail tooltip — rendered as a sibling INSIDE a `group/tip` trigger button.
 * Only visible when collapsed to the desktop rail; hidden on mobile. */
function Tip({ label, railed }: { label: string; railed: boolean }) {
  if (!railed) return null;
  return (
    <span className="pointer-events-none absolute left-[calc(100%+14px)] top-1/2 z-[90] hidden -translate-y-1/2 whitespace-nowrap rounded-[7px] border border-white/[0.09] bg-[#0d2014] px-2.5 py-1.5 text-[12px] font-medium text-[#eafff2] shadow-[0_8px_22px_rgb(0_0_0/0.4)] min-[881px]:group-hover/tip:block">
      {label}
    </span>
  );
}
